// localStorage keys + tiny getters that only touch them.
// Everything else in lib/mr/ pulls STORAGE from this module so the key
// catalogue stays single-source-of-truth.

export const STORAGE = {
  HINT: "mr_show_hint",
  NOTE_TITLE: "mr_note_title",
  NOTE_BODY: "mr_note_body",
  NOTE_DRAWING: "mr_note_drawing", // data URL of the canvas content
  EFFECT_MODE: "mr_effect_mode",   // "speak" | "draw" | "type"
  LANGUAGE: "mr_lang",              // BCP-47 code or "auto"
  DECOY_NOTES: "mr_decoy_notes",    // JSON array of {title, preview}
  DECOY_TRASH: "mr_decoy_trash",    // JSON array of soft-deleted notes {…decoy, deletedAt}
  DECOY_FOLDERS: "mr_decoy_folders", // JSON array of spectator-visible custom folders {id, name}
  DECOY_SHUFFLE_COUNT: "mr_decoy_shuffle_count", // integer 1..MAX_DECOYS, last-used Shuffle count
  AUTH_TOKEN: "mr_auth_token",      // shared password used as X-App-Auth header
  IS_ADMIN: "mr_is_admin",          // "1" | "0" — was the unlock token the master password?
  PREDICTION_MODE: "mr_prediction_mode", // legacy, kept for migration only
  REVEAL_STYLE: "mr_reveal_style",  // "peek" | "scroll" | "whisper"
  PREDICTION_PREFIX: "mr_prediction_prefix", // free-text, prepended before the AI answer in scroll
  BACKDATE_MIN: "mr_backdate_min",   // integer 0..180 — minutes to rewind the note timestamp
  DEFAULT_TITLE: "mr_default_title", // text — the title every fresh trick note opens with
  COMPOSE_TITLE: "mr_compose_title", // text — the title preset for the (+) compose button on /notes
  COMPOSE_EFFECT: "mr_compose_effect", // "type" | "draw" — what input the (+) button opens with
  COMPACT_REVEAL: "mr_compact_reveal", // "1" | "0" — abbreviate dates/zodiac/weekdays in the subliminal answer
  // --- Swiper (top decoy prediction via clock-swipe encoding on /notes) ---
  SWIPER_ENABLED: "mr_swiper_enabled",          // "1" | "0"
  SWIPER_MODE:    "mr_swiper_mode",             // "cards" | "custom"
  SWIPER_FORMAT:  "mr_swiper_format",           // "long" | "symbol" | "custom" (cards mode only)
  SWIPER_TEMPLATE:"mr_swiper_template",         // sentence with (xxx) placeholder
  SWIPER_WORD_U:  "mr_swiper_word_u",           // legacy single-direction word (migrated to COMBOS)
  SWIPER_WORD_R:  "mr_swiper_word_r",           // legacy single-direction word (migrated to COMBOS)
  SWIPER_WORD_D:  "mr_swiper_word_d",           // legacy single-direction word (migrated to COMBOS)
  SWIPER_WORD_L:  "mr_swiper_word_l",           // legacy single-direction word (migrated to COMBOS)
  SWIPER_CUSTOM_COMBOS: "mr_swiper_custom_combos", // JSON [{id, dirs, word}] — magician-defined combo→word map
  SWIPER_CARD:    "mr_swiper_card",             // transient: JSON {value, suit, custom?} captured during performance
  // --- Ten Outs (third decoy — cycling word locked on phone-flip) ---
  TENOUTS_ENABLED:  "mr_tenouts_enabled",    // "1" | "0"
  TENOUTS_INTERVAL: "mr_tenouts_interval",   // integer seconds, range 2..10
  TENOUTS_TEMPLATE: "mr_tenouts_template",   // sentence with (xxx) placeholder
  TENOUTS_WORDS:    "mr_tenouts_words",      // JSON string array (up to 10)
  TENOUTS_LOCKED:   "mr_tenouts_locked",     // transient: word at the moment of flip
  // --- Add-a-Number (compose / + button — three spectators add up) ---
  ADDNUM_ENABLED:       "mr_addnum_enabled",       // "1" | "0"
  ADDNUM_FORCE_TOTAL:   "mr_addnum_force_total",   // integer — the magician's pre-written sum
  ADDNUM_SWAP_POSITION: "mr_addnum_swap_position", // "1" | "2" | "3" — which row gets rewritten
  ADDNUM_TRIGGER:       "mr_addnum_trigger",       // "done" | "handoff" | "placedown"
  // --- Trident (second decoy — three numbered lines, reveal in the order
  //     the spectator wrote them, NOT the visual top-to-bottom order) ---
  TRIDENT_ENABLED:  "mr_trident_enabled",   // "1" | "0"
  TRIDENT_TEMPLATE: "mr_trident_template",  // template with (a) (b) (c) placeholders
  // --- Notes Apocalypse (fake iOS lockscreen → passcode-coded card →
  //     delayed "hacked phone" glitch where every visible note title
  //     rewrites itself into a forced sentence) -------------------------
  APOCALYPSE_ENABLED:        "mr_apocalypse_enabled",
  APOCALYPSE_LENGTH:         "mr_apocalypse_length",
  APOCALYPSE_DELAY:          "mr_apocalypse_delay",
  APOCALYPSE_TEMPLATE:       "mr_apocalypse_template",
  APOCALYPSE_WALLPAPER:      "mr_apocalypse_wallpaper",
  APOCALYPSE_ARMED:          "mr_apocalypse_armed",
  // --- Acrostic Notes (magician arms via flashlight-icon on the fake
  //     lockscreen, then enters card as passcode. On unlock the notes
  //     list has its middle N titles quietly rewritten so their FIRST
  //     LETTERS spell the card word, e.g. 7♥ → SEVENHEARTS = 11 notes.
  //     Titles still look mundane; body/preview stay coherent.) --------
  ACROSTIC_ENABLED:  "mr_acrostic_enabled",  // "1" | "0" — global on/off (defaults to on)
  ACROSTIC_CARD:     "mr_acrostic_card",     // transient: JSON {value, suit} captured on unlock
  // Phase-0 (wipe — reverse-hack delete of preset note titles) -------
  APOCALYPSE_WIPE_FRAME_MS:       "mr_apocalypse_wipe_frame_ms",
  APOCALYPSE_POST_WIPE_DELAY_MS:  "mr_apocalypse_post_wipe_delay_ms",
  // Reset gesture (shake-to-reset) -----------------------------------
  APOCALYPSE_SHAKE_THRESHOLD: "mr_apocalypse_shake_threshold",
  APOCALYPSE_SHAKE_COUNT:     "mr_apocalypse_shake_count",
  APOCALYPSE_AUTO_MOTION:     "mr_apocalypse_auto_motion", // "1" / "0" — auto requestPermission on first lockscreen tap
  // Notification ping sound (Web Audio tri-tone fired on each extra spawn)
  APOCALYPSE_SOUND_ENABLED:   "mr_apocalypse_sound_enabled", // "1" / "0", default "1"
  APOCALYPSE_SOUND_VOLUME:    "mr_apocalypse_sound_volume",  // 0..100 master
  // Fake iOS push-message banners overlaid during the glitch
  APOCALYPSE_PUSH_ENABLED:    "mr_apocalypse_push_enabled",  // "1" / "0", default "1"
  APOCALYPSE_PUSH_SOUND_ENABLED: "mr_apocalypse_push_sound_enabled", // "1" / "0", default "1"
  APOCALYPSE_PUSH_INTERVAL_MS:"mr_apocalypse_push_interval_ms", // gap between successive banners
  APOCALYPSE_PUSH_DURATION_MS:"mr_apocalypse_push_duration_ms", // on-screen lifetime per banner
  APOCALYPSE_PUSH_MESSAGES:   "mr_apocalypse_push_messages", // JSON array of {app, title, body}
  // Native FX abuses -------------------------------------------------
  // (1) System permission prompt theatre
  APOCALYPSE_FX_PERM_ENABLED: "mr_apocalypse_fx_perm_enabled",
  APOCALYPSE_FX_PERM_TYPE:    "mr_apocalypse_fx_perm_type", // camera|microphone|notifications|geolocation
  // (2) Speech-synthesis reveal
  APOCALYPSE_FX_SPEECH_ENABLED:  "mr_apocalypse_fx_speech_enabled",
  APOCALYPSE_FX_SPEECH_TEMPLATE: "mr_apocalypse_fx_speech_template",
  APOCALYPSE_FX_SPEECH_VOICE:    "mr_apocalypse_fx_speech_voice", // voiceURI
  APOCALYPSE_FX_SPEECH_RATE:     "mr_apocalypse_fx_speech_rate",  // 50..200 (stored ×100)
  APOCALYPSE_FX_SPEECH_PITCH:    "mr_apocalypse_fx_speech_pitch", // 0..200 (stored ×100)
  // (3) Clipboard hijack
  APOCALYPSE_FX_CLIPBOARD_ENABLED:  "mr_apocalypse_fx_clipboard_enabled",
  APOCALYPSE_FX_CLIPBOARD_TEMPLATE: "mr_apocalypse_fx_clipboard_template",
  // (4) Theme-color status bar flicker
  APOCALYPSE_FX_THEME_ENABLED:  "mr_apocalypse_fx_theme_enabled",
  APOCALYPSE_FX_THEME_COLORS:   "mr_apocalypse_fx_theme_colors", // JSON array of hex
  APOCALYPSE_FX_THEME_INTERVAL: "mr_apocalypse_fx_theme_interval",
  // Post-shake reset behaviour --------------------------------------
  APOCALYPSE_RESET_PRESET:      "mr_apocalypse_reset_preset",      // "instant" | "flash"
  APOCALYPSE_FLASH_DELAY_MS:    "mr_apocalypse_flash_delay_ms",    // wait after empty beat before flashes (only in "flash" preset)
  APOCALYPSE_FLASH_COUNT:       "mr_apocalypse_flash_count",       // 1..3 white flashes
  APOCALYPSE_FLASH_DURATION_MS: "mr_apocalypse_flash_duration_ms", // per-flash duration
  APOCALYPSE_FLASH_GAP_MS:      "mr_apocalypse_flash_gap_ms",      // off-time between consecutive flashes
  APOCALYPSE_CUE_DOT_POSITION: "mr_apocalypse_cue_dot_position",
  APOCALYPSE_CUE_DOT_SIZE_PX: "mr_apocalypse_cue_dot_size_px",
  APOCALYPSE_CUE_DOT_COLOR: "mr_apocalypse_cue_dot_color",
  APOCALYPSE_CUE_DOT_OPACITY_PCT: "mr_apocalypse_cue_dot_opacity_pct",
  APOCALYPSE_CUE_DOT_LEAD_SEC: "mr_apocalypse_cue_dot_lead_sec",
  APOCALYPSE_REHEARSING:       "mr_apocalypse_rehearsing", // "1" while a Settings-triggered rehearsal is in flight
  // Pass-4 additions: wipe mode + post-reveal error dialog + second
  // prediction template + two-shake reveal mode.
  APOCALYPSE_WIPE_MODE:        "mr_apocalypse_wipe_mode",        // "one-by-one" | "flash"
  APOCALYPSE_ERROR_ENABLED:    "mr_apocalypse_error_enabled",    // "1" / "0", default "1"
  APOCALYPSE_ERROR_TITLE:      "mr_apocalypse_error_title",      // alert title
  APOCALYPSE_ERROR_BODY:       "mr_apocalypse_error_body",       // alert body text
  APOCALYPSE_ERROR_SOUND_VOLUME: "mr_apocalypse_error_sound_volume", // 0..100
  // Vibration bed — MP3 loop that mimics an iPhone-in-silent-mode buzz.
  // Plays for the entire haunt phase and cuts dead the instant the
  // error dialog raises. Managed as an <audio ref> in DecoyNotes.
  APOCALYPSE_VIBRATION_ENABLED: "mr_apocalypse_vibration_enabled", // "1" / "0", default "1"
  APOCALYPSE_VIBRATION_VOLUME:  "mr_apocalypse_vibration_volume",  // 0..100, default 60
  APOCALYPSE_SECOND_TEMPLATE:  "mr_apocalypse_second_template",  // template w/ (xxx) for the 2nd reveal
  APOCALYPSE_SHAKE_MODE:       "mr_apocalypse_shake_mode",       // "one-shake" | "two-shake"
  // Reveal scroll speed: ms duration of the per-row scrollIntoView during
  // the resolve loop. Lower = faster; 0 = instant snap.
  APOCALYPSE_SCROLL_SPEED_MS:  "mr_apocalypse_scroll_speed_ms",   // 0..2000, default 600
  // Post-restore "1-second flash" — after the final shake clears, the
  // preset decoy note bodies briefly show the SECOND prediction text
  // before flashing back to their original previews.
  APOCALYPSE_RESTORE_FLASH_ENABLED: "mr_apocalypse_restore_flash_enabled", // "1" / "0", default "1"
  APOCALYPSE_RESTORE_FLASH_MS: "mr_apocalypse_restore_flash_ms",  // 0..5000, default 1000
  // Phase-1 (extras) tuning + custom title pool ----------------------
  APOCALYPSE_EXTRAS_COUNT:    "mr_apocalypse_extras_count",
  APOCALYPSE_EXTRAS_INTERVAL: "mr_apocalypse_extras_interval",
  APOCALYPSE_EXTRAS_TITLES:   "mr_apocalypse_extras_titles",
  // Phase-2 (flicker) tuning ----------------------------------------
  APOCALYPSE_FLICKER_MS:      "mr_apocalypse_flicker_ms",
  APOCALYPSE_FLASH_1:         "mr_apocalypse_flash_1",
  APOCALYPSE_FLASH_2:         "mr_apocalypse_flash_2",
  APOCALYPSE_FLASH_3:         "mr_apocalypse_flash_3",
  APOCALYPSE_FLASH_4:         "mr_apocalypse_flash_4",
  // Phase-3 (resolve) tuning ----------------------------------------
  APOCALYPSE_ROW_STAGGER:     "mr_apocalypse_row_stagger",
  APOCALYPSE_ROW_CYCLE:       "mr_apocalypse_row_cycle",
  APOCALYPSE_ROW_FRAME:       "mr_apocalypse_row_frame",
  // --- Phantom Peek (silent capture of a +-created note's contents that
  //     the spectator typed and then swipe-deleted; magician triggers the
  //     reveal during the phone-handover beat by tapping the "Notes" h1) -
  PEEK_ENABLED:      "mr_peek_enabled",        // "1" | "0"
  PEEK_DURATION_MS:  "mr_peek_duration_ms",    // 500..5000, default 2000
  PEEK_PASS_MOTION:  "mr_peek_pass_motion",    // "1" | "0" — also fire on pass-motion burst
  PEEK_PASS_THRESHOLD: "mr_peek_pass_threshold", // 6..18 m/s^2 — burst magnitude
  // --- Flip Force (phone held face-down ≥X s, on flip face-up the
  //     magician's chosen target decoy note is force-opened) ---
  FLIP_FORCE_ENABLED:        "mr_flip_force_enabled",        // "1" | "0"
  FLIP_FORCE_TARGET_ID:      "mr_flip_force_target_id",      // decoy.id of the FIRST target (convincer)
  FLIP_FORCE_TARGET_ID_2:    "mr_flip_force_target_id_2",    // decoy.id of the SECOND target (kicker). Empty = single-stage trick.
  FLIP_FORCE_TARGET_ID_3:    "mr_flip_force_target_id_3",    // decoy.id of the THIRD target (final). Empty = two-stage trick.
  FLIP_FORCE_MIN_FACEDOWN_MS:"mr_flip_force_min_facedown_ms",// integer 500..10000, default 2000
  FLIP_FORCE_STAGE:          "mr_flip_force_stage",          // "0" | "1" | "2" | "3" — sequence cursor. 0=next fires note 1, 1=next fires note 2, 2=next fires note 3, 3=stopped.
  // --- Search-Force Prediction (fake "old" note injected atop search
  //     results from the very first typed letter; body reveals a
  //     forced sentence with (xxx) replaced by the typed query) -----
  SEARCH_FORCE_ENABLED:      "mr_search_force_enabled",      // "1" | "0"
  SEARCH_FORCE_TEMPLATE:     "mr_search_force_template",     // body text with (xxx) + (card) + (date) placeholders
  SEARCH_FORCE_TITLE:        "mr_search_force_title",        // title of the fake prediction note
  SEARCH_FORCE_DATE_LABEL:   "mr_search_force_date_label",   // LEGACY free-form OLD-looking metadata date (kept for backward compat / fallback)
  SEARCH_FORCE_DATE_ISO:     "mr_search_force_date_iso",     // ISO datetime for the "note date (top of note)" — drives BOTH the displayed header date AND the folder bucketing so both stay in sync
  SEARCH_FORCE_CARD:         "mr_search_force_card",         // lockscreen-armed card as JSON {value,suit} (legacy: plain label)
  SEARCH_FORCE_ARMED_AT:     "mr_search_force_armed_at",     // ms timestamp of lockscreen arm — kept for future use only
  SEARCH_FORCE_DATE_OFFSET_MINUTES:
                             "mr_search_force_date_offset_minutes", // integer minutes; drives (date) placeholder = now - N
  // --- Three-question personalisation (lockscreen Cancel/Emergency taps
  //     during the searchforce arm session). Answers themselves are
  //     stored as a JSON array under SEARCH_FORCE_ANSWERS. The six
  //     LABEL keys hold the magician-configured word that gets pushed
  //     into that answer array depending on which side was tapped.
  SEARCH_FORCE_Q1_CANCEL_LABEL:
                             "mr_search_force_q1_cancel_label",     // e.g. "morning"
  SEARCH_FORCE_Q1_EMERGENCY_LABEL:
                             "mr_search_force_q1_emergency_label",  // e.g. "evening"
  SEARCH_FORCE_Q2_CANCEL_LABEL:
                             "mr_search_force_q2_cancel_label",     // e.g. "cats"
  SEARCH_FORCE_Q2_EMERGENCY_LABEL:
                             "mr_search_force_q2_emergency_label",  // e.g. "dogs"
  SEARCH_FORCE_Q3_CANCEL_LABEL:
                             "mr_search_force_q3_cancel_label",     // e.g. "tea"
  SEARCH_FORCE_Q3_EMERGENCY_LABEL:
                             "mr_search_force_q3_emergency_label",  // e.g. "coffee"
  SEARCH_FORCE_Q4_CANCEL_LABEL:
                             "mr_search_force_q4_cancel_label",     // e.g. "introvert"
  SEARCH_FORCE_Q4_EMERGENCY_LABEL:
                             "mr_search_force_q4_emergency_label",  // e.g. "extrovert"
  SEARCH_FORCE_ANSWERS:      "mr_search_force_answers",             // JSON array of the 4 collected labels
  // Frozen ISO timestamp captured at ARM time (either lockscreen
  // decode or Settings-enable). Overrides the live `now - offset`
  // computation so the note's (date) placeholder stays consistent
  // even if the spectator opens the note hours later.
  SEARCH_FORCE_FROZEN_DATE_ISO:
                             "mr_search_force_frozen_date_iso",
  // Session-persistent prediction. Once the spectator opens the
  // search-force row in /trick we snapshot the fully-rendered note
  // {title, body, dateISO, cardLabel, spectator} and inject it into
  // the regular folder list under the day-bucket matching its
  // dateISO. Cleared on any new arm / disarm.
  SEARCH_FORCE_PERSISTED:    "mr_search_force_persisted",
  // --- Realism: haptic feedback (Web Vibration API) ---------------
  // Buzzes on every meaningful tap so the PWA feels native. iOS Safari
  // silently no-ops the API today; Android + installed PWAs do fire.
  // Magician can disable per-preference from Settings.
  HAPTICS_ENABLED:           "mr_haptics_enabled",           // "1" | "0", default "1"
  // --- Realism: decoy notes reset-on-exit ------------------------
  // When a spectator opens an existing decoy note and types something,
  // the edit is discarded on Back so the next show sees the original
  // magician-configured content again. Compose (+) notes are unaffected.
  DECOY_RESET_ON_EXIT:       "mr_decoy_reset_on_exit",       // "1" | "0", default "1"
  // --- Realism: iCloud "storage almost full" banner --------------
  // Native-iOS looking banner rendered directly under the note count
  // ("X notities"). Purely cosmetic — sells the disguise. Default ON.
  ICLOUD_BANNER_ENABLED:     "mr_icloud_banner_enabled",     // "1" | "0", default "1"
  // --- Prediction share link ("Share as note") --------------------
  // Magician publishes a snapshot of the current prediction and
  // AirDrops the /n/{code} URL to the spectator BEFORE the show. As
  // long as the snapshot is unlocked, every relevant setting change
  // (SearchForce config, spectator answers, armed card) auto-syncs
  // to the backend so the shared link stays in sync until the
  // magician taps Lock (or 6h auto-lock kicks in) — after that the
  // snapshot is frozen forever.
  PREDICTION_LINK_ACTIVE_CODE: "mr_prediction_link_active_code", // string | ""
  PREDICTION_LINK_AUTO_SYNC:   "mr_prediction_link_auto_sync",   // "1" | "0", default "1"
  // -------------------------------------------------------------------
  // Which UI wrapper does /n/{code} render? Currently:
  //   "note"     → plain iOS-Notes look (default, minimal)
  //   "akinator" → fortune-teller genie reveal flow. Spectator taps
  //                start → crystal-ball intro → genie names them
  //                (uses (xxx)) → 5 fake questions → custom-reveal
  //                story with (q1)/(q2)/(q3)/(q4)/(card) substitutions.
  // Custom-reveal template is the magician-authored story used at the
  // very end of the akinator flow. Supports the same placeholders as
  // the Search-Force paper note.
  PREDICTION_LINK_THEME:          "mr_prediction_link_theme",
  PREDICTION_LINK_CUSTOM_REVEAL:  "mr_prediction_link_custom_reveal",
};

/** Read the magician-configured default title that every fresh trick note opens with. */
export function getDefaultTitle() {
  return localStorage.getItem(STORAGE.DEFAULT_TITLE) || "";
}

/** Title preset for the (+) compose button on /notes. Falls back to DEFAULT_TITLE. */
export function getComposeTitle() {
  const v = localStorage.getItem(STORAGE.COMPOSE_TITLE);
  // Null means never set => fall back. Empty string means user explicitly cleared it.
  if (v === null) return getDefaultTitle();
  return v;
}

/** True when multi-question mode applies to this entry point. Multi-question
 *  is now permanently tied to the (+) compose button — every other entry
 *  point gets a single-question note. */
export function isMultiQuestion() {
  return false;
}

/**
 * Resolve the reveal style, migrating the legacy `PREDICTION_MODE` flag if
 * the user hasn't set the new key yet. The result is always one of
 * "peek" | "scroll" | "whisper". (The old Speak-only "prediction" style
 * was removed when Speak input was retired.)
 */
export function getRevealStyle() {
  const v = localStorage.getItem(STORAGE.REVEAL_STYLE);
  if (v === "peek" || v === "scroll" || v === "whisper") return v;
  return "peek";
}

/** True when the magician opted for the abbreviated one-liner subliminal. */
export function isCompactReveal() {
  return localStorage.getItem(STORAGE.COMPACT_REVEAL) === "1";
}

/**
 * Install a global axios interceptor that attaches the cached password to
 * every outgoing request so backend endpoints behind require_app_password
 * always receive the X-App-Auth header.
 */
export function installAxiosAuth(axiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE.AUTH_TOKEN);
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-App-Auth"] = token;
    }
    return config;
  });
}
